import { useMutation } from '@tanstack/react-query'

import { PayloadError } from '@/constants/payload-error'
import { GET_PROJECTS_QUERY } from '@/hooks/queries/use-get-projects'
import { useAuth } from '@/hooks/use-auth'
import { DefaultProjectFormData } from '@/pages/projects-form/contexts/project-context'
import { createProject } from '@/services/create-project'
import { createRegions, Region } from '@/services/create-regions'
import { queryClient } from '@/services/query-client'

export const useCreateProject = () => {
  const { user } = useAuth()

  const mutation = useMutation(
    async (payload: DefaultProjectFormData) => {
      const { id, regions } = await createProject({
        name: payload.project_name,
        owner: user?.name,
        address: {
          city: payload.city,
          state: payload.state.name,
        },
        regions: payload.regions.map((region) => ({ name: region.name })),
      })

      try {
        const regionsPayload = payload.regions
          .filter((region) => region.geojson)
          .map<Region>((region, index) => {
            return {
              project_id: id,
              region_id: regions[index].id,
              geojson: region.geojson,
              col_name: region.col_name!,
              removed_features: [],
              region_name: region.name,
            }
          })

        if (regionsPayload.length) {
          await createRegions(regionsPayload)
        }
      } catch (error: any) {
        throw new PayloadError(error?.message, { id }, { cause: error })
      }

      return { id }
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([GET_PROJECTS_QUERY])
      },
    },
  )

  return mutation
}
